import { CheckCircle, Globe, Image, Copy, Download } from "react-feather";
import { Feed, TypeMapping } from "../../types/api/Feed";
import { FieldValues, UseFormSetValue, UseFormWatch } from "react-hook-form";
import { formatLocation } from "../../shared/formatLocation";
import { getValidTypes } from "../../utils/postTypes";
import { ContentCheck } from "../PostCompose/ContentCheck";
import { AddFiles, SelectedFile } from "../../types/PostSelection";
import { useContext, useState } from "react";
import { AuthContext } from "../../AuthProvider";
import { supabase } from "../../supabaseClient";
import { Post } from "../../types/api/Post";
import { PostsForFeed } from "../../types/api/PostsForFeeds";
import { PublishButton } from "./PublishButton";
import { ViewPostLink } from "./ViewPostLink";
import { usePostPublishing } from "../../hooks/usePostPublishing";
import { PLATFORM_SLUGS } from "./socialPlatforms";
import cx from "classnames";
import {
  formatPostContent,
  SavedDescriptionForPost,
} from "../../shared/formatPost";
import { useProfile } from "../../state/db/profile";
import { useSavedDescriptions } from "../../state/db/savedDescriptions";
import { useSelectedSavedDescriptions } from "../../state/hooks/useSelectedSavedDescriptions";
import { usePositionManagement } from "../PostCompose/usePositionManagement";
import { PanoramaSlicer } from "../PanoramaSlicer";
import {
  download_files,
  getFeedPhotoDownloads,
} from "../../utils/downloadFiles";
import { handleError } from "../../utils/handleError";

export function PostDisplay({
  feed,
  watch,
  editFeed,
  typeMapping,
  selectedFiles,
  setValue,
  currentPost,
  showPublishActions = false,
  addFiles,
}: {
  feed: Feed;
  watch: UseFormWatch<FieldValues>;
  editFeed: () => void;
  typeMapping?: TypeMapping;
  selectedFiles?: SelectedFile[];
  setValue: UseFormSetValue<FieldValues>;
  currentPost?: Post;
  showPublishActions?: boolean;
  addFiles?: AddFiles;
}) {
  const { session } = useContext(AuthContext);
  const { profile } = useProfile();
  const { savedDescriptions } = useSavedDescriptions();
  const { selectedSavedDescriptions } = useSelectedSavedDescriptions(
    `selectedSavedDescriptions-${feed.slug}`,
  );
  const { positions } = usePositionManagement(selectedSavedDescriptions);

  console.log(
    "POST DISPLAY - SELECTED SAVED DESCRIPTIONS:",
    selectedSavedDescriptions,
  );

  // Find the corresponding post for feed from currentPost
  const postForFeed = currentPost?.posts_for_feeds?.find(
    (pff) => (pff.feed as Feed).id === feed.id,
  );

  const [postingStatus, setPostingStatus] = useState(postForFeed?.published);
  const [downloading, setDownloading] = useState(false);

  const { posting, handleSocialMediaPost } = usePostPublishing({
    session,
    currentPost,
    postForFeed,
    onPublishSuccess: (pff) => publishPost(pff, true),
  });

  const postContent = watch(`${feed.slug}.post_content`);
  const hashtags = watch(`${feed.slug}.hashtags`) || [];
  const location = watch(`${feed.slug}.location`);
  const postType = watch(`${feed.slug}.type`);
  const autoPublish = watch(`${feed.slug}.auto_publish`);
  const disableAutoPublish = watch(`disable_auto_publish`);
  const userSelectedFiles = watch(`${feed.slug}.selected_files`);

  const isEmbeddedLocation = feed.location_type === "EMBED";
  const locationIfExists = isEmbeddedLocation
    ? formatLocation(location, true)
    : undefined;

  console.log("SAVED DESCRIPTIONS FOR POST: BEFORE", savedDescriptions);
  const ordered = [
    ...selectedSavedDescriptions.map((id) =>
      savedDescriptions.find((desc) => desc?.id === id),
    ),
    ...savedDescriptions.filter(
      (desc) => !selectedSavedDescriptions.includes(desc?.id),
    ),
  ].filter((desc) => !!desc);

  const savedDescriptionsForPost: SavedDescriptionForPost[] = ordered.map(
    (desc) => ({
      id: desc.id!,
      slug: desc.slug!,
      content: desc.variants[feed.slug!] ?? desc.content,
      position: positions.content[desc.id!] ?? desc.position ?? "INLINE",
      selected: selectedSavedDescriptions.includes(desc.id!),
      hashtags: desc.hashtags.variants[feed.slug!] ?? desc.hashtags.main,
      hashtagsPosition:
        positions.hashtag[desc.id!] ?? desc.hashtag_position ?? "START",
      tagsPosition: positions.tag[desc.id!] ?? desc.tag_position ?? "START",
    }),
  );
  console.log("SAVED DESCRIPTIONS FOR POST: AFTER", savedDescriptionsForPost);
  const template = feed.post_template_override ?? profile?.post_template ?? "";
  const content = postWithLocationAndHashtags(
    template,
    postContent,
    hashtags,
    locationIfExists,
    savedDescriptionsForPost,
  );

  const validTypes = getValidTypes([postType], typeMapping);

  const publishPost = async (p: PostsForFeed, disableCopy?: boolean) => {
    if (!disableCopy) {
      navigator.clipboard.writeText(content);
    }

    const { data: updatedPostForFeed } = await supabase
      .from("posts_for_feeds")
      .update({
        published: !postingStatus,
      })
      .eq("id", p.id)
      .select(`*, feed(*)`)
      .single();

    console.log("Updated post for feed:", updatedPostForFeed);

    if (updatedPostForFeed) {
      setPostingStatus(updatedPostForFeed.published);
    }
  };

  const downloadFeedPhotos = async () => {
    if (!currentPost?.posts_media) return;

    setDownloading(true);
    try {
      const selectedIds = (postForFeed?.selectedFiles ?? []).filter(
        (id): id is number => typeof id === "number",
      );
      const selectedMedia =
        selectedIds.length > 0
          ? currentPost.posts_media.filter((media) =>
              selectedIds.includes(media.id),
            )
          : currentPost.posts_media;
      const photos = selectedMedia.filter((media) =>
        media.mine_type?.startsWith("image/"),
      );

      if (photos.length === 0) {
        throw new Error(`No photos are selected for ${feed.name}`);
      }

      const downloads = await getFeedPhotoDownloads({
        feed,
        postType,
        media: photos,
      });
      await download_files(downloads);
    } catch (error) {
      handleError(error as Error);
    } finally {
      setDownloading(false);
    }
  };

  return (
    <div className="mb-6">
      <div className="flex justify-between items-center mb-2">
        <div className="prose">
          <h2>{feed.name}</h2>
        </div>
      </div>

      <div className="card bg-base-100">
        <div className="card-body py-4 px-6">
          <div className="flex flex-col md:flex-row justify-between items-start md:items-center gap-2">
            <div className="flex items-center gap-2">
              {postType && (
                <div className="badge badge-outline">
                  <Image size={16} /> {validTypes[0]?.name}
                </div>
              )}
              {autoPublish && !disableAutoPublish && (
                <div className="badge badge-outline badge-success">
                  <CheckCircle size={16} />
                  Auto-Publish
                </div>
              )}
              {autoPublish && disableAutoPublish && (
                <div className="badge badge-outline badge-warning">
                  <CheckCircle size={16} />
                  Auto-Publish Disabled (All Feeds)
                </div>
              )}
              {feed.auto_publish && !autoPublish && (
                <div className="badge badge-outline badge-warning">
                  <CheckCircle size={16} />
                  Auto-Publish Disabled (Only This Feed)
                </div>
              )}
            </div>

            {feed.location_type !== "EMBED" && location && (
              <div className="flex items-center gap-2">
                <Globe size={16} />
                <span>{formatLocation(location)}</span>
              </div>
            )}
          </div>

          <hr className="my-2" />

          <ContentCheck
            feed={feed}
            selectedFiles={selectedFiles}
            userSelectedFiles={userSelectedFiles}
            postType={postType}
            setValue={setValue}
            content={postContent}
            hashtags={hashtags}
            location={locationIfExists}
            formattedContent={content}
          />

          {addFiles && selectedFiles?.length === 1 && (
            <PanoramaSlicer
              feed={feed}
              sourceFile={selectedFiles[0]}
              watch={watch}
              setValue={setValue}
            />
          )}

          <div className="mb-4">
            <p>
              {content.split("\n").map((line, index) => (
                <span key={index}>
                  {line}
                  {index < content.split("\n").length - 1 && <br />}
                </span>
              ))}
            </p>
          </div>

          <hr className="my-2" />

          <div className="space-y-4">
            <div className="flex flex-row justify-between items-center gap-2">
              <div>
                <button
                  type="button"
                  className="btn btn-primary"
                  onClick={editFeed}
                >
                  Edit
                </button>
              </div>

              {showPublishActions && postForFeed && (
                <div className="flex flex-col sm:flex-row items-end sm:items-center gap-2 flex-wrap">
                  {feed.slug === PLATFORM_SLUGS.THREADS && (
                    <PublishButton
                      platform="threads"
                      isLoading={posting.threads}
                      onClick={() => handleSocialMediaPost("threads")}
                      disabled={posting.threads}
                    />
                  )}
                  {feed.slug === PLATFORM_SLUGS.INSTAGRAM && (
                    <PublishButton
                      platform="instagram"
                      isLoading={posting.instagram}
                      onClick={() => handleSocialMediaPost("instagram")}
                      disabled={posting.instagram}
                    />
                  )}
                  {feed.slug === PLATFORM_SLUGS.FACEBOOK && (
                    <PublishButton
                      platform="facebook"
                      isLoading={posting.facebook}
                      onClick={() => handleSocialMediaPost("facebook")}
                      disabled={posting.facebook}
                    />
                  )}
                  {feed.slug === PLATFORM_SLUGS.BLUESKY && (
                    <PublishButton
                      platform="bluesky"
                      isLoading={posting.bluesky}
                      onClick={() => handleSocialMediaPost("bluesky")}
                      disabled={posting.bluesky}
                    />
                  )}
                  {(feed.slug === PLATFORM_SLUGS.MASTODON ||
                    feed.auto_post_as === PLATFORM_SLUGS.MASTODON) && (
                    <PublishButton
                      platform="mastodon"
                      label={`Post to ${feed.name}`}
                      isLoading={posting[feed.slug || "mastodon"]}
                      onClick={() =>
                        handleSocialMediaPost("mastodon", feed!.slug)
                      }
                      disabled={posting[feed.slug || "mastodon"]}
                    />
                  )}
                  <button
                    type="button"
                    className="btn btn-outline"
                    onClick={downloadFeedPhotos}
                    disabled={downloading}
                  >
                    {downloading ? (
                      <span className="loading loading-spinner loading-sm" />
                    ) : (
                      <Download size={16} />
                    )}
                    Download for {feed.name}
                  </button>
                  <button
                    type="button"
                    className={cx(
                      "btn",
                      postingStatus ? "btn-success" : "btn-primary",
                    )}
                    onClick={() => publishPost(postForFeed)}
                  >
                    {postingStatus ? (
                      <>
                        <CheckCircle size={16} /> Published
                      </>
                    ) : (
                      <>
                        <Copy size={16} /> Copy Content
                      </>
                    )}
                  </button>

                  <ViewPostLink feed={feed} postForFeed={postForFeed} />
                </div>
              )}
            </div>
          </div>
        </div>
      </div>
    </div>
  );
}

export function postWithLocationAndHashtags(
  template: string,
  postContent: string,
  hashtags: string[],
  location?: string,
  savedDescriptions: SavedDescriptionForPost[] = [],
): string {
  const data = {
    content: postContent,
    hashtags: {
      andTags: hashtags,
      first: hashtags?.filter((tag) => tag.startsWith("#"))[0],
      all: hashtags.filter((tag) => tag.startsWith("#")),
      tags: hashtags.filter((tag) => tag.startsWith("@")),
    },
    embeddedLocation: !!location,
    location: location,
  };

  console.log("POST DISPLAY - DATA:", data, savedDescriptions);

  return formatPostContent(template, data, savedDescriptions);
}
